import { FaTrash, FaRegEnvelope, FaPhoneAlt, FaUser } from "react-icons/fa";
import { useEffect, useState } from "react";
import BASE_URL from "../../BASEURL";

export default function EnquiryList() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch Enquiries
  useEffect(() => {
    async function fetchEnquiries() {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${BASE_URL}/api/inquiry`);
        if (!res.ok) throw new Error("Failed to fetch enquiries");
        const result = await res.json();
        if (result && result.success && Array.isArray(result.data)) {
          setEnquiries(result.data);
        } else {
          setEnquiries([]);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch enquiries");
      } finally {
        setLoading(false);
      }
    }
    fetchEnquiries();
  }, []);

  // Delete handler
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this enquiry?")) return;
    try {
      const res = await fetch(`${BASE_URL}/api/inquiry/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete enquiry");
      setEnquiries((prev) => prev.filter((e) => e._id !== id && e.id !== id));
    } catch (err) {
      alert(err.message || "Failed to delete enquiry");
    }
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 ">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">
            Customer <span className="text-blue-600">Inquiries</span>
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Manage and respond to your customer messages.</p>
        </div>
        {enquiries.length > 0 && (
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm border border-gray-200">
            <span className="text-gray-600 font-medium">Total: </span>
            <span className="text-blue-600 font-bold">{enquiries.length}</span>
          </div>
        )}
      </div>

      {/* Error & Loading States */}
      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded shadow-sm flex items-center gap-3">
          <span>{error}</span>
        </div>
      )}

      {/* Table Container */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50/50 border-b border-gray-100">
                <th className="p-4 font-semibold text-gray-700 uppercase text-xs tracking-wider">Customer</th>
                <th className="p-4 font-semibold text-gray-700 uppercase text-xs tracking-wider">Contact Info</th>
                <th className="p-4 font-semibold text-gray-700 uppercase text-xs tracking-wider">Message</th>
                <th className="p-4 font-semibold text-gray-700 uppercase text-xs tracking-wider text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                // Skeleton Loader
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {[...Array(4)].map((_, j) => (
                      <td key={j} className="p-4"><div className="h-4 bg-gray-200 rounded w-full"></div></td>
                    ))}
                  </tr>
                ))
              ) : enquiries.length > 0 ? (
                enquiries.map((e) => (
                  <tr key={e._id || e.id} className="hover:bg-blue-50/30 transition-colors duration-200">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                          <FaUser size={14} />
                        </div>
                        <span className="font-semibold text-gray-800">{e.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <FaRegEnvelope className="text-gray-400" size={12} />
                          <span className="truncate max-w-[150px]">{e.email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaPhoneAlt className="text-gray-400" size={12} />
                          <span>{e.phone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-sm text-gray-600 line-clamp-2 max-w-xs bg-gray-50 p-2 rounded italic">
                        "{e.message}"
                      </p>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => handleDelete(e._id || e.id)}
                        className="p-3 text-red-500 hover:bg-red-50 hover:text-red-700 rounded-full transition-all duration-200 group"
                        title="Delete Enquiry"
                      >
                        <FaTrash className="transform group-hover:scale-110" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="p-10 text-center text-gray-400 italic">
                    No enquiries found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}